/**
 * Pan 3D showcase — GSAP ScrollTrigger + Three.js (per `[data-pan-showcase-root]`).
 * Reads JSON from `<script type="application/json" data-pan-config>`.
 */
(function () {
  'use strict';

  var DEG = Math.PI / 180;
  var LERP_SPEED = 0.07;

  var DEFAULT_INTRO_POSES = [
    { rotX: 0, rotY: -180, rotZ: 90, scale: 0.85, camZ: 4.5 },
    { rotX: 0, rotY: 0, rotZ: 90, scale: 0.85, camZ: 4.5 },
  ];

  var DEFAULT_REASON_POSES = [
    { rotX: 115, rotY: 1, rotZ: 50, scale: 0.85, camZ: 4.5 },
    { rotX: 115, rotY: 1, rotZ: -58, scale: 0.85, camZ: 4.5 },
    { rotX: 149, rotY: 15, rotZ: 1, scale: 0.85, camZ: 4.5 },
    { rotX: 180, rotY: 0, rotZ: -90, scale: 0.85, camZ: 4.5 },
  ];

  function buildStages(introCount, reasonCount, poseOverrides) {
    var stages = [];
    var slot = 0;
    var i;

    for (i = 0; i < introCount; i++) {
      var ibase = DEFAULT_INTRO_POSES[Math.min(i, DEFAULT_INTRO_POSES.length - 1)];
      var im =
        poseOverrides && poseOverrides[slot]
          ? Object.assign({}, ibase, poseOverrides[slot])
          : Object.assign({}, ibase);
      im.frameIndex = i;
      stages.push(im);
      slot++;
    }

    for (var r = 0; r < reasonCount; r++) {
      var rb =
        DEFAULT_REASON_POSES[
          Math.min(r, DEFAULT_REASON_POSES.length - 1)
        ];
      var rm =
        poseOverrides && poseOverrides[slot]
          ? Object.assign({}, rb, poseOverrides[slot])
          : Object.assign({}, rb);
      rm.frameIndex = introCount + 1 + r;
      stages.push(rm);
      slot++;
    }

    return stages;
  }

  function defaultStageBreaks(numStages) {
    var preset = [0, 0.15, 0.35, 0.55, 0.7, 0.85, 1.01];
    if (numStages === 6) return preset.slice();
    var b = [];
    for (var i = 0; i <= numStages; i++) {
      if (numStages <= 1) b.push(i);
      else b.push(i / numStages);
    }
    b[0] = 0;
    b[numStages] = 1.01;
    return b;
  }

  function showFallback(root, canvas, fallbackImg) {
    if (canvas) canvas.style.display = 'none';
    if (fallbackImg) {
      fallbackImg.style.display = 'block';
      fallbackImg.classList.add('is-visible');
    }
    if (!fallbackImg && root) root.classList.add('pan-showcase--static-fallback');
  }

  function mountPanShowcase(sectionRoot) {
    var cfg = {};
    try {
      var cfgEl = sectionRoot.querySelector(
        'script[type="application/json"][data-pan-config]',
      );
      if (cfgEl && cfgEl.textContent) cfg = JSON.parse(cfgEl.textContent.trim());
    } catch (e) {
      console.warn('[PanShowcase] Bad JSON theme config:', e);
    }

    var introCount = Math.max(parseInt(cfg.introCount, 10) || 2, 1);
    var reasonCount = Math.max(parseInt(cfg.reasonCount, 10) || 1, 1);
    var poses = cfg.poses;
    var gltfUrl = typeof cfg.gltfUrl === 'string' ? cfg.gltfUrl.trim() : '';

    var canvas = sectionRoot.querySelector('.pan-showcase__canvas');
    var fallbackImg = sectionRoot.querySelector('.pan-showcase__fallback');
    var scrollDriver = sectionRoot.querySelector('.pan-showcase__scroll-driver');
    var contentZone = sectionRoot.querySelector('.pan-showcase__content-zone');
    var textTrack = sectionRoot.querySelector('.pan-showcase__text-track');
    var frames = Array.prototype.slice.call(
      sectionRoot.querySelectorAll('.pan-showcase__frame'),
    );

    if (!canvas || !scrollDriver) return;

    if (typeof THREE === 'undefined') {
      console.warn('[PanShowcase] THREE not loaded.');
      showFallback(sectionRoot, canvas, fallbackImg);
      return;
    }
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.warn('[PanShowcase] GSAP / ScrollTrigger not loaded.');
      showFallback(sectionRoot, canvas, fallbackImg);
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    var STAGES = buildStages(introCount, reasonCount, poses);
    var NUM_STAGES = STAGES.length;

    var disposed = false;
    var desktopMql = window.matchMedia('(min-width: 768px)');
    var panShowcaseScrollTrigger = null;
    var activeStageBreaks = defaultStageBreaks(NUM_STAGES).slice();

    var lastReasonDomIndex = introCount + reasonCount;
    var headerDomIndex = introCount;

    function isDesktopTextScrollMode() {
      return desktopMql.matches;
    }

    function frameOffsetInTrack(frameEl) {
      if (!frameEl || !textTrack) return 0;
      var y = 0;
      var el = frameEl;
      while (el && el !== textTrack) {
        y += el.offsetTop;
        el = el.parentElement;
      }
      return y;
    }

    function refreshActiveStageBreaks() {
      var preset = defaultStageBreaks(NUM_STAGES).slice();

      if (
        introCount < 2 ||
        !isDesktopTextScrollMode() ||
        !textTrack ||
        !contentZone ||
        !frames[1] ||
        !frames[lastReasonDomIndex]
      ) {
        activeStageBreaks = preset;
        return;
      }

      var travel = Math.max(
        0,
        textTrack.scrollHeight - contentZone.clientHeight,
      );
      if (travel < 32) {
        activeStageBreaks = preset;
        return;
      }

      var lead = Math.min(contentZone.clientHeight * 0.14, 56);

      function progressWhen(idx) {
        var el = frames[idx];
        if (!el) return 0;
        var y = Math.max(0, frameOffsetInTrack(el) - lead);
        return Math.max(0, Math.min(1, y / travel));
      }

      var b = [0];
      b.push(Math.max(0.04, Math.min(0.24, progressWhen(1) * 0.92)));

      var caps = [0.44, 0.64, 0.82, 0.97];
      for (var rt = 0; rt < reasonCount; rt++) {
        var idxDom = introCount + 1 + rt;
        var pAt = progressWhen(idxDom);
        var cap = caps[Math.min(rt, caps.length - 1)];
        if (reasonCount > caps.length) {
          cap = Math.min(0.97 + (rt - caps.length + 1) * 0.008, 0.995);
        }
        var prev = b[b.length - 1];
        var sep = rt === 0 ? 0.03 : 0.02;
        b.push(Math.max(prev + sep, Math.min(cap, pAt)));
      }
      b.push(1.01);

      for (var k = 1; k < b.length; k++) {
        if (b[k] <= b[k - 1]) b[k] = Math.min(1, b[k - 1] + 0.03);
      }
      b[b.length - 1] = 1.01;
      activeStageBreaks = b;
    }

    function getStageIndexFromProgress(progress) {
      var breaks = activeStageBreaks;
      var stageIdx = NUM_STAGES - 1;
      for (var i = 0; i < breaks.length - 1; i++) {
        if (progress < breaks[i + 1]) {
          stageIdx = i;
          break;
        }
      }
      return Math.min(stageIdx, NUM_STAGES - 1);
    }

    function adjustScrollDriverHeightForDesktop() {
      if (
        !isDesktopTextScrollMode() ||
        !scrollDriver ||
        !contentZone ||
        !textTrack
      ) {
        scrollDriver.style.height = '';
        activeStageBreaks = defaultStageBreaks(NUM_STAGES).slice();
        return;
      }
      requestAnimationFrame(function () {
        var travel = Math.max(
          0,
          textTrack.scrollHeight - contentZone.clientHeight,
        );
        var vh = window.innerHeight;
        var extra = Math.max(vh * 1.05, travel * 1.2);
        scrollDriver.style.height = '' + Math.round(vh + extra) + 'px';
        refreshActiveStageBreaks();
        if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
        if (panShowcaseScrollTrigger) onScrollProgress(panShowcaseScrollTrigger.progress);
      });
    }

    function updateDesktopTextTrack(progress) {
      if (
        !isDesktopTextScrollMode() ||
        !textTrack ||
        !contentZone ||
        typeof gsap === 'undefined'
      )
        return;
      var travel = Math.max(
        0,
        textTrack.scrollHeight - contentZone.clientHeight,
      );
      gsap.set(textTrack, { y: -progress * travel });
    }

    var renderer;
    var scene;
    var camera;
    var panModel = null;
    var panPivot = null;
    var animFrameId = 0;
    var webglOk = false;
    var baseFitScale = 1;

    var current = { rotX: 0, rotY: -180, rotZ: 90, scale: 0.85, camZ: 4.5 };
    var target = { rotX: 0, rotY: -180, rotZ: 90, scale: 0.85, camZ: 4.5 };

    function applyStageToTarget(idx) {
      var s = STAGES[Math.max(0, Math.min(idx, NUM_STAGES - 1))];
      target.rotX = s.rotX;
      target.rotY = s.rotY;
      target.rotZ = s.rotZ;
      target.scale = s.scale;
      target.camZ = s.camZ;
    }

    function initThreeJS() {
      try {
        var rightPanel = canvas.parentElement;
        var rect = rightPanel ? rightPanel.getBoundingClientRect() : { width: 0, height: 0 };
        var w = Math.round(rect.width) || 640;
        var h = Math.round(rect.height) || 720;

        renderer = new THREE.WebGLRenderer({
          canvas: canvas,
          antialias: true,
          alpha: true,
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(w, h);
        renderer.outputEncoding = THREE.sRGBEncoding;
        renderer.shadowMap.enabled = false;
        renderer.setClearColor(0x000000, 0);

        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(45, w / h, 0.01, 100);
        camera.position.set(0, 0, current.camZ);

        var keyLight = new THREE.DirectionalLight(0xfff5e4, 2.0);
        keyLight.position.set(3, 5, 5);
        scene.add(keyLight);

        var fillLight = new THREE.DirectionalLight(0xd0e8ff, 0.6);
        fillLight.position.set(-4, -2, 3);
        scene.add(fillLight);

        var rimLight = new THREE.DirectionalLight(0xffffff, 0.4);
        rimLight.position.set(0, -5, -5);
        scene.add(rimLight);

        var ambLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(ambLight);

        webglOk = true;
        loadGLTF();
      } catch (err) {
        console.warn('[PanShowcase] WebGL init failed:', err);
        showFallback(sectionRoot, canvas, fallbackImg);
      }
    }

    function loadGLTF() {
      if (!webglOk) return;
      var LoaderClass = THREE.GLTFLoader;
      if (!LoaderClass) {
        console.warn('[PanShowcase] GLTFLoader missing.');
        showFallback(sectionRoot, canvas, fallbackImg);
        return;
      }
      if (!gltfUrl) {
        console.warn('[PanShowcase] No GLTF URL in section settings.');
        showFallback(sectionRoot, canvas, fallbackImg);
        return;
      }

      var loader = new LoaderClass();

      /** Shopify-hosted GLBs often use KHR_draco_mesh_compression; GLTFLoader needs DRACOLoader + WASM path. */
      if (THREE.DRACOLoader) {
        var dracoDecoderPath =
          'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/libs/draco/gltf/';
        try {
          var dracoLoader = new THREE.DRACOLoader();
          dracoLoader.setDecoderPath(dracoDecoderPath);
          loader.setDRACOLoader(dracoLoader);
        } catch (e) {
          console.warn('[PanShowcase] DRACOLoader setup failed:', e);
        }
      } else {
        console.warn(
          '[PanShowcase] DRACOLoader script missing. Draco-compressed GLBs (common on Shopify Files) cannot decode.',
        );
      }

      loader.load(
        gltfUrl,
        function onLoad(gltf) {
          if (disposed) return;
          panModel = gltf.scene;
          panModel.rotation.x = -Math.PI / 2;

          panPivot = new THREE.Group();
          panPivot.add(panModel);
          scene.add(panPivot);

          panModel.updateWorldMatrix(true, true);

          var box = new THREE.Box3().setFromObject(panModel);
          var size = new THREE.Vector3();
          var centre = new THREE.Vector3();
          box.getSize(size);
          box.getCenter(centre);

          var maxDim = Math.max(size.x, size.y, size.z);

          var desiredSize = 0.8;
          var width = window.innerWidth;
          if (width < 768) desiredSize = 0.83;
          else if (width < 992) desiredSize = 0.7;

          var fitScale = maxDim > 0 ? desiredSize / maxDim : 1;
          baseFitScale = fitScale;
          panModel.scale.setScalar(fitScale);

          panModel.updateWorldMatrix(true, true);

          box.setFromObject(panModel);
          box.getCenter(centre);
          panModel.position.set(-centre.x, -centre.y, -centre.z);
          panModel.updateWorldMatrix(true, true);

          applyStageToTarget(1);
          renderLoop();
        },
        undefined,
        function onError(err) {
          console.warn('[PanShowcase] GLTF load error:', err);
          showFallback(sectionRoot, canvas, fallbackImg);
        },
      );
    }

    function renderLoop() {
      if (disposed) return;
      animFrameId = requestAnimationFrame(renderLoop);
      if (!panPivot || !renderer) return;

      current.rotX += (target.rotX - current.rotX) * LERP_SPEED;
      current.rotY += (target.rotY - current.rotY) * LERP_SPEED;
      current.rotZ += (target.rotZ - current.rotZ) * LERP_SPEED;
      current.scale += (target.scale - current.scale) * LERP_SPEED;
      current.camZ += (target.camZ - current.camZ) * LERP_SPEED;

      panPivot.rotation.x = current.rotX * DEG;
      panPivot.rotation.y = current.rotY * DEG;
      panPivot.rotation.z = current.rotZ * DEG;
      panPivot.scale.setScalar(baseFitScale * current.scale);
      camera.position.z = current.camZ;

      renderer.render(scene, camera);
    }

    function onResize() {
      if (!renderer || !camera) return;
      var panel = canvas.parentElement;
      if (!panel) return;
      var w = panel.clientWidth;
      var h = panel.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }

    window.addEventListener('resize', onResize);

    var activeFrameIndex = -1;

    function showFrame(newIndex) {
      if (isDesktopTextScrollMode()) return;
      if (newIndex === activeFrameIndex) return;

      var isMobileAnim = window.innerWidth < 768;
      var isBackward = newIndex < activeFrameIndex;

      var isGroupA = newIndex < introCount;
      var groupAIndices = [];
      var gi;
      for (gi = 0; gi < introCount; gi++) groupAIndices.push(gi);

      var groupBIndices = [headerDomIndex];
      for (var gr = 0; gr < reasonCount; gr++) {
        groupBIndices.push(introCount + 1 + gr);
      }

      var wasGroupA = activeFrameIndex < introCount && activeFrameIndex >= 0;
      var crossingBoundary =
        (isGroupA && !wasGroupA) || (!isGroupA && wasGroupA);

      if (activeFrameIndex === -1 && newIndex === 0) {
        var initialFrame = frames[0];
        if (initialFrame) {
          initialFrame.classList.add('is-active');
          gsap.killTweensOf(initialFrame);
          gsap.set(initialFrame, { display: 'flex', opacity: 1, x: 0 });
        }
        activeFrameIndex = 0;
        return;
      }

      function revealFrame(fr, delay, activeClass) {
        delay = typeof delay === 'number' ? delay : 0;
        activeClass = activeClass || 'is-active';
        if (fr.classList.contains(activeClass)) return;
        fr.classList.remove('is-active', 'pan_frame_active');
        fr.classList.add(activeClass);
        gsap.killTweensOf(fr);
        var mobileFade = window.innerWidth < 768;
        gsap.set(fr, {
          display: 'flex',
          opacity: 0,
          x: mobileFade ? 0 : -60,
          y: 0,
        });
        gsap.to(fr, {
          opacity: 1,
          x: 0,
          y: 0,
          duration: 0.6,
          delay: delay,
          ease: 'power2.out',
        });
      }

      function hideFrame(fr, force) {
        if (
          !fr ||
          (!fr.classList.contains('is-active') &&
            !fr.classList.contains('pan_frame_active'))
        )
          return;
        fr.classList.remove('is-active', 'pan_frame_active');
        gsap.killTweensOf(fr);

        if (force) gsap.set(fr, { display: 'none', opacity: 0 });
        else {
          var mobileFade = window.innerWidth < 768;
          gsap.to(fr, {
            opacity: 0,
            x: mobileFade ? 0 : -20,
            y: 0,
            duration: 0.3,
            ease: 'power2.in',
            onComplete: function () {
              gsap.set(fr, { display: 'none' });
            },
          });
        }
      }

      if (isGroupA) {
        if (crossingBoundary || isBackward) {
          groupBIndices.forEach(function (idx) {
            var fr = frames[idx];
            if (fr) hideFrame(fr, true);
          });
        }

        groupAIndices.forEach(function (idx) {
          var fr = frames[idx];
          if (!fr) return;
          if (isMobileAnim) {
            idx === newIndex ? revealFrame(fr, 0) : hideFrame(fr, true);
          } else {
            if (idx === newIndex) {
              var delay = wasGroupA ? 0.35 : 0;
              revealFrame(fr, delay);
            } else hideFrame(fr, isBackward);
          }
        });
      } else {
        if (crossingBoundary || isBackward) {
          groupAIndices.forEach(function (idx) {
            var fr = frames[idx];
            if (fr) hideFrame(fr, true);
          });
        }

        groupBIndices.forEach(function (idx) {
          var fr = frames[idx];
          if (!fr) return;
          if (idx === headerDomIndex) {
            revealFrame(fr);
            return;
          }
          if (isMobileAnim) {
            idx === newIndex
              ? revealFrame(fr, 0, 'pan_frame_active')
              : hideFrame(fr, true);
          } else {
            idx <= newIndex ? revealFrame(fr) : hideFrame(fr, isBackward);
          }
        });
      }

      activeFrameIndex = newIndex;
    }

    function syncLayoutMode() {
      if (isDesktopTextScrollMode()) {
        frames.forEach(function (fr) {
          gsap.killTweensOf(fr);
        });
        adjustScrollDriverHeightForDesktop();
        if (panShowcaseScrollTrigger) {
          updateDesktopTextTrack(panShowcaseScrollTrigger.progress || 0);
        }
      } else {
        activeStageBreaks = defaultStageBreaks(NUM_STAGES).slice();
        scrollDriver.style.height = '';
        if (textTrack && typeof gsap !== 'undefined') {
          gsap.set(textTrack, { clearProps: 'transform' });
        }
        var p = panShowcaseScrollTrigger ? panShowcaseScrollTrigger.progress : 0;
        var stageIdx = getStageIndexFromProgress(p);
        showFrame(STAGES[stageIdx].frameIndex);
        if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
      }
    }

    function onScrollProgress(progress) {
      var clampedP = Math.max(0, Math.min(1, progress));
      var startStage = STAGES[1];
      var endStage = STAGES[NUM_STAGES - 1];

      if (NUM_STAGES < 2 || !startStage || !endStage) return;

      target.rotX =
        startStage.rotX + (endStage.rotX - startStage.rotX) * clampedP;
      target.rotY =
        startStage.rotY + (endStage.rotY - startStage.rotY) * clampedP;
      target.rotZ =
        startStage.rotZ + (endStage.rotZ - startStage.rotZ) * clampedP;
      target.scale =
        startStage.scale + (endStage.scale - startStage.scale) * clampedP;
      target.camZ =
        startStage.camZ + (endStage.camZ - startStage.camZ) * clampedP;

      var stageIdx = getStageIndexFromProgress(progress);
      updateDesktopTextTrack(progress);
      if (!isDesktopTextScrollMode()) {
        showFrame(STAGES[stageIdx].frameIndex);
      }
    }

    function setupScrollTrigger() {
      panShowcaseScrollTrigger = ScrollTrigger.create({
        id:
          'pan-showcase-scroll-' +
          String(sectionRoot.dataset.sectionId || sectionRoot.id || 'instance').replace(
            /\W/g,
            '-',
          ),
        trigger: scrollDriver,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1.5,
        onUpdate: function (self) {
          onScrollProgress(self.progress);
        },
      });

      if (isDesktopTextScrollMode()) {
        adjustScrollDriverHeightForDesktop();
        updateDesktopTextTrack(panShowcaseScrollTrigger.progress);
      } else showFrame(0);
    }

    var resizeTimer;
    function onPanShowcaseResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(syncLayoutMode, 120);
    }

    window.addEventListener('resize', onPanShowcaseResize);

    if (typeof desktopMql.addEventListener === 'function') {
      desktopMql.addEventListener('change', syncLayoutMode);
    } else if (typeof desktopMql.addListener === 'function') {
      desktopMql.addListener(syncLayoutMode);
    }

    if (
      typeof document !== 'undefined' &&
      document.fonts &&
      document.fonts.ready
    ) {
      document.fonts.ready.then(function () {
        if (isDesktopTextScrollMode()) {
          adjustScrollDriverHeightForDesktop();
          if (panShowcaseScrollTrigger)
            updateDesktopTextTrack(panShowcaseScrollTrigger.progress);
        }
      });
    }

    function destroy() {
      disposed = true;
      if (resizeTimer) clearTimeout(resizeTimer);
      if (animFrameId) cancelAnimationFrame(animFrameId);
      if (panShowcaseScrollTrigger) panShowcaseScrollTrigger.kill();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('resize', onPanShowcaseResize);
      if (typeof desktopMql.removeEventListener === 'function')
        desktopMql.removeEventListener('change', syncLayoutMode);
      else if (typeof desktopMql.removeListener === 'function')
        desktopMql.removeListener(syncLayoutMode);
      if (renderer)
        try {
          renderer.dispose();
        } catch (e) {}
    }

    sectionRoot.addEventListener('shopify:section:unload', destroy, {
      once: true,
    });

    initThreeJS();
    setupScrollTrigger();
  }

  /** Auto-boot all sections on DOM ready — matches deferred script order after DOM. */
  function bootAll() {
    var roots = document.querySelectorAll('[data-pan-showcase-root]');
    for (var i = 0; i < roots.length; i++) mountPanShowcase(roots[i]);
  }

  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', bootAll, { once: true });
  else bootAll();
})();
